import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MobileMenuIcon from './MobileMenuIcon';

const activeStyles = 'bg-blue-gray-200 bg-opacity-50 text-black';
const defaultStyles =
    'hover:text-blue-gray-800 text-blue-gray-700 hover:bg-blue-gray-200 hover:bg-opacity-30 active:bg-blue-gray-300';

export default function MobileMenuItem({
    item,
    navigateToUrl,
    isActive,
    activeSubnavItem,
}) {
    const [isSubnavOpen, setSubnavOpen] = useState(false);

    useEffect(() => {
        setSubnavOpen(isActive && item?.subNav?.length > 0);
    }, [isActive]);

    if (item.hideInAlli360) {
        return null;
    }

    const parentActive = isActive && !activeSubnavItem;

    return (
        <>
            <div className="w-full">
                {item?.subNav?.length > 0 ? (
                    <div
                        role="button"
                        aria-pressed={isSubnavOpen}
                        className={classNames(
                            'block flex items-center h-auto group py-3 h-11 pl-5 pr-3 rounded cursor-pointer mb-3 w-full focus:outline-none focus-visible:ring-2',
                            {
                                [activeStyles]: parentActive,
                                [defaultStyles]: parentActive,
                            },
                        )}
                        onClick={() => setSubnavOpen(!isSubnavOpen)}
                        aria-hidden="true"
                    >
                        {item?.iconPath && (
                            <MobileMenuIcon active={isActive} item={item} />
                        )}
                        <div className="flex">
                            <div className="w-36 ml-3">
                                {item.alli360Name || item.name}
                            </div>
                            <div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="w-4 h-4 transition-transform transform ease-out"
                                    style={{
                                        transform: `${
                                            !isSubnavOpen
                                                ? 'rotate(0deg)'
                                                : 'rotate(90deg)'
                                        }`,
                                    }}
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                ) : (
                    <CreateLink
                        item={item}
                        navigateToUrl={navigateToUrl}
                        className={classNames(
                            'block group py-3 h-11 pl-5 pr-3 rounded cursor-pointer mb-3 w-full focus:outline-none focus-visible:ring-2',
                            {
                                'bg-blue-gray-200 bg-opacity-50 text-black':
                                    isActive,
                                'hover:text-blue-gray-800 text-blue-gray-700 hover:bg-blue-gray-200 hover:bg-opacity-30 active:bg-blue-gray-300':
                                    !isActive,
                            },
                        )}
                    >
                        <div className="flex items-center h-4">
                            <MobileMenuIcon active={isActive} item={item} />
                            <div className="ml-3">
                                {item.alli360Name || item.name}
                            </div>
                        </div>
                    </CreateLink>
                )}
            </div>
            <div
                className={classNames(
                    'overflow-hidden max-h-0 my-1 opacity-0 transition-all ease-in-out duration-300',
                    isSubnavOpen && 'max-h-screen h-auto opacity-100',
                )}
            >
                {item?.subNav?.map((subItem, idx) => (
                    <CreateLink
                        key={idx}
                        item={subItem}
                        navigateToUrl={navigateToUrl}
                        className={classNames(
                            'mb-1 last:mb-0 w-full flex cursor-pointer pl-12 pr-3 rounded-md gap-2.5 py-2 hover:bg-blue-gray-100',
                            {
                                [activeStyles]: activeSubnavItem === subItem.id,
                            },
                        )}
                    >
                        <span className="text-blue-gray-800 whitespace-nowrap">
                            {subItem.name}
                        </span>
                    </CreateLink>
                ))}
            </div>
        </>
    );
}

MobileMenuItem.propTypes = {
    item: PropTypes.shape({
        href: PropTypes.string.isRequired,
        name: PropTypes.node.isRequired,
        iconPath: PropTypes.node.isRequired,
        isSpa: PropTypes.bool,
    }).isRequired,
    navigateToUrl: PropTypes.func.isRequired,
    isActive: PropTypes.bool.isRequired,
    activeSubnavItem: PropTypes.string,
};

export function CreateLink({ item, navigateToUrl, children, ...props }) {
    // things in SPA need to be handled via `navigateToUrl` so the whole
    // page doesn't re-render.
    if (item.isSpa) {
        return (
            <a href={item.href} onClick={navigateToUrl} {...props}>
                {children}
            </a>
        );
    }

    if (item.target) {
        props.target = item.target;
    }

    return (
        <a href={item.href} {...props}>
            {children}
        </a>
    );
}
